import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  InputBase,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Schedule as ScheduleIcon,
  AddCircleOutlineOutlined,
  SearchOutlined,
  FilterListOutlined,
  Edit,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import AddEquipmentDialog from "./AddEquipment";
import ScheduleMaintenanceDialog from "./ScheduleMaintenanceDialog";
import EquipmentDetailDialog from "./ViewEquipment";
import MaintenanceScheduleDialog from "./ViewMaintenanceSchedule";
import type { IEquipment, IMaintenanceSchedule } from "../../types/equipment";
import type { GridColDef } from "@mui/x-data-grid";
import { DataGrid } from "../../components/common/Index";
import { tokens } from "../../theme";
import apiService from "../../service/Api";

const ListEquipment: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("All Equip");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [equipmentDetailOpen, setEquipmentDetailOpen] = useState(false);
  const [maintenanceScheduleOpen, setMaintenanceScheduleOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] =
    useState<IMaintenanceSchedule | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<IEquipment | null>(
    null
  );
  const [editingEquipment, setEditingEquipment] = useState<IEquipment | null>(
    null
  );
  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedEquipmentForSchedule, setSelectedEquipmentForSchedule] =
    useState<IEquipment | null>(null);

  // Hardcoded maintenance schedules (replace with API call if needed)
  const [maintenanceSchedules, setMaintenanceSchedules] = useState<
    IMaintenanceSchedule[]
  >([
    {
      id: "ms1",
      equipmentId: "1",
      title: "Quarterly Camera Check",
      type: "Preventive",
      startDate: "2025-07-01",
      endDate: "2025-07-01",
      isAllDay: true,
      assignedTo: "John Tech",
      details: "Routine inspection for Sony Alpha A6400",
      expectedDowntime: "2 hours",
      priority: "Medium",
      notificationReminder: "1 day before",
    },
    // ... (other maintenance schedules as in your original code)
  ]);

  // Fetch equipment from backend
  const fetchEquipment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.get<{ results: any[] }>(
        "/api/equipments/equipment/"
      );
      const equipmentData: IEquipment[] = response.data.results.map((item) => ({
        id: item.id.toString(),
        serialNo: item.serial_number || item.serialNo || "",
        name: item.name || "",
        brand: item.brand || "",
        category: item.category || "",
        subCategory: item.sub_category || item.subCategory || "",
        purchaseDate: item.purchase_date || item.purchaseDate || "",
        status: item.status || "",
        nextMaintenance:
          item.next_maintenance || item.nextMaintenance || undefined,
        lastServiceDate:
          item.last_service_date || item.lastServiceDate || undefined,
        hoursSinceLastService:
          item.hours_since_last_service ||
          item.hoursSinceLastService ||
          undefined,
        assignee: item.assignee || undefined,
      }));
      setEquipmentList(equipmentData);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to load equipment. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEditEquipment = (equipment: IEquipment) => {
    setEditingEquipment(equipment);
    setAddDialogOpen(true);
  };

  const handleAddEquipment = async (equipment: Omit<IEquipment, "id">) => {
    try {
      setError(null);
      if (editingEquipment) {
        // Update existing equipment
        const payload = {
          serial_number: equipment.serialNo,
          name: equipment.name,
          brand: equipment.brand,
          category: equipment.category,
          sub_category: equipment.subCategory,
          purchase_date: equipment.purchaseDate,
          status: equipment.status,
          next_maintenance: equipment.nextMaintenance,
          last_service_date: equipment.lastServiceDate,
          hours_since_last_service: equipment.hoursSinceLastService,
          assignee: equipment.assignee,
        };
        await apiService.put(
          `/api/equipments/equipment/${editingEquipment.id}/`,
          payload
        );
        setEditingEquipment(null);
      } else {
        // Add new equipment
        const payload = {
          serial_number: equipment.serialNo,
          name: equipment.name,
          brand: equipment.brand,
          category: equipment.category,
          sub_category: equipment.subCategory,
          purchase_date: equipment.purchaseDate,
          status: equipment.status,
          next_maintenance: equipment.nextMaintenance,
          last_service_date: equipment.lastServiceDate,
          hours_since_last_service: equipment.hoursSinceLastService,
          assignee: equipment.assignee,
        };
        await apiService.post("/api/equipments/equipment/", payload);
      }
      // Refresh equipment list
      await fetchEquipment();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to save equipment. Please try again.";
      setError(errorMessage);
    } finally {
      setAddDialogOpen(false);
    }
  };

  const handleScheduleMaintenance = (
    schedule: Omit<IMaintenanceSchedule, "id">
  ) => {
    setScheduleDialogOpen(false);
  };

  const filteredEquipment = equipmentList.filter(
    (equipment) =>
      (equipment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        equipment.serialNo.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (filterCategory === "All Equip" || equipment.category === filterCategory)
  );

  // Add filtering for maintenance schedules
  const filteredMaintenanceSchedules = maintenanceSchedules.filter(
    (schedule) => {
      // Find the equipment for this schedule
      const equipment = equipmentList.find(
        (eq) => eq.id === schedule.equipmentId
      );
      // Check if the schedule matches the search term (title or type)
      const matchesSearch =
        schedule.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.type.toLowerCase().includes(searchTerm.toLowerCase());
      // Check if the schedule matches the filter category
      const matchesCategory =
        filterCategory === "All Equip" ||
        (equipment && equipment.category === filterCategory);
      return matchesSearch && matchesCategory;
    }
  );

  const columns: GridColDef[] = [
    { field: "serialNo", headerName: "Serial No.", flex: 1 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "brand", headerName: "Brand", flex: 1 },
    { field: "category", headerName: "Category", flex: 1 },
    { field: "subCategory", headerName: "Sub Category", flex: 1 },
    { field: "nextMaintenance", headerName: "Next Maintenance", flex: 1 },
    {
      field: "edit",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" width="100%">
          <Tooltip title="Edit Equipment" arrow>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                const equipment = params.row;
                handleEditEquipment(equipment);
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      align: "center",
      headerAlign: "center",
    },
  ];

  const maintenanceColumns: GridColDef[] = [
    { field: "title", headerName: "Title", flex: 1 },
    { field: "type", headerName: "Type", flex: 1 },
    { field: "startDate", headerName: "Start Date", flex: 1 },
    { field: "endDate", headerName: "End Date", flex: 1 },
    { field: "assignedTo", headerName: "Technician", flex: 1 },
    { field: "priority", headerName: "Priority", flex: 1 },
  ];

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box
              sx={{
                backgroundColor: colors.primary[900],
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                pl: 1,
                width: "100%",
              }}
            >
              <SearchOutlined sx={{ mr: 1 }} />
              <InputBase
                placeholder="Search by name, brand"
                sx={{ ml: 1, flex: 1 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton>
                <FilterListOutlined />
              </IconButton>
            </Box>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel
                sx={{
                  color: colors.primary[300],
                  "&.Mui-focused": {
                    color: "#614438",
                  },
                }}
              >
                Filter
              </InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Filter"
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid ${colors.primary[300]}`,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#614438",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                  backgroundColor: colors.primary[900],
                }}
              >
                <MenuItem value="All Equip">All Equip</MenuItem>
                <MenuItem value="Camera">Camera</MenuItem>
                <MenuItem value="Audio">Audio</MenuItem>
                <MenuItem value="Lighting">Lighting</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<ScheduleIcon />}
              onClick={() => setScheduleDialogOpen(true)}
            >
              Schedule Maintenance
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineOutlined />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Equipment
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress sx={{ color: colors.primary[500] }} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={fetchEquipment}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Tabs */}
        {!isLoading && !error && (
          <Box sx={{ mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab
                label="Equipment Inventory"
                sx={{
                  fontWeight: activeTab === 0 ? "bold" : "normal",
                  borderBottom: activeTab === 0 ? "2px solid #8B4513" : "none",
                }}
              />
              <Tab
                label="Maintenance"
                sx={{
                  fontWeight: activeTab === 1 ? "bold" : "normal",
                  borderBottom: activeTab === 1 ? "2px solid #8B4513" : "none",
                }}
              />
            </Tabs>
          </Box>
        )}

        {/* Content */}
        {!isLoading && !error && (
          <AnimatePresence mode="wait">
            {activeTab === 0 && (
              <motion.div
                key="equipment-grid"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <DataGrid
                  rows={filteredEquipment}
                  columns={columns}
                  getRowId={(row) => row.id}
                  navigateToOnRowClick={() => ""}
                  onRowClick={(params) => {
                    setSelectedEquipment(params.row);
                    setEquipmentDetailOpen(true);
                  }}
                />
              </motion.div>
            )}

            {activeTab === 1 && (
              <motion.div
                key="maintenance-grid"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <DataGrid
                  rows={filteredMaintenanceSchedules}
                  columns={maintenanceColumns}
                  getRowId={(row) => row.id}
                  onRowClick={(params) => {
                    setSelectedSchedule(params.row);
                    setMaintenanceScheduleOpen(true);
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Dialogs */}
        <AddEquipmentDialog
          open={addDialogOpen}
          onClose={() => {
            setAddDialogOpen(false);
            setEditingEquipment(null);
          }}
          onSubmit={handleAddEquipment}
          equipment={editingEquipment}
          isEdit={!!editingEquipment}
        />

        <ScheduleMaintenanceDialog
          open={scheduleDialogOpen}
          onClose={() => {
            setScheduleDialogOpen(false);
            setSelectedEquipmentForSchedule(null);
          }}
          onSubmit={handleScheduleMaintenance}
          equipmentList={equipmentList}
          selectedEquipmentId={selectedEquipmentForSchedule?.id}
        />

        <EquipmentDetailDialog
          open={equipmentDetailOpen}
          onClose={() => setEquipmentDetailOpen(false)}
          equipment={selectedEquipment}
          onScheduleMaintenance={() => {
            setEquipmentDetailOpen(false);
            setSelectedEquipmentForSchedule(selectedEquipment);
            setScheduleDialogOpen(true);
          }}
        />

        <MaintenanceScheduleDialog
          open={maintenanceScheduleOpen}
          onClose={() => setMaintenanceScheduleOpen(false)}
          schedule={selectedSchedule}
        />
      </motion.div>
    </Box>
  );
};

export default ListEquipment;
